/**
 * CLIENT-SIDE LOGGER
 *
 * This logger is designed for browser/React components and includes:
 * - Browser-specific metadata (URL, user agent, screen resolution, etc.)
 * - Automatic Sentry reporting: error becomes an Issue, all levels feed Logs
 * - Console output with [CLIENT-*] prefixes
 *
 * Usage:
 * ```typescript
 * import { createLogger } from '@/utils/logger';
 * const logger = createLogger('MyComponent');
 *
 * logger.debug('Debug message');     // Console only
 * logger.info('Info message');       // Console only
 * logger.warn('Warning message');    // Console + Database
 * logger.error('Error message', {    // Console + Database
 *   error: errorObject,
 *   customData: 'additional info'
 * });
 * ```
 *
 * Environment Variables:
 * - NEXT_PUBLIC_LOG_LEVEL: Set exact log level (trace, debug, info, warn, error, fatal, silent)
 *   Example: NEXT_PUBLIC_LOG_LEVEL=info (in .env.local)
 * - NEXT_PUBLIC_DEV_MODE: Enable/disable dev mode (used as fallback if LOG_LEVEL not set)
 *
 * For SERVER-SIDE logging (SSR, API routes), use @/utils/ssr-logger instead.
 */

import pino from 'pino';
import * as Sentry from '@sentry/nextjs';

// CLIENT-SIDE LOGGER - For browser/React components
// Create logger based on environment
const isDev = process.env.NEXT_PUBLIC_DEV_MODE === 'true';

type LogLevel = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal' | 'silent';

// Get log level from environment variable with fallback
const getLogLevel = (): LogLevel => {
  const envLogLevel = process.env.NEXT_PUBLIC_LOG_LEVEL;
  const validLevels: LogLevel[] = ['trace', 'debug', 'info', 'warn', 'error', 'fatal', 'silent'];

  // If environment variable is set and valid, use it
  if (envLogLevel && validLevels.includes(envLogLevel.toLowerCase() as LogLevel)) {
    return envLogLevel.toLowerCase() as LogLevel;
  }

  // Otherwise, use default behavior: debug in dev, silent in production
  return isDev ? 'debug' : 'silent';
};

// Get client-side metadata
const getClientMetadata = (): Record<string, string | null> => {
  if (typeof window === 'undefined') return {};

  return {
    environment: 'client',
    userAgent: navigator.userAgent,
    url: window.location.href,
    pathname: window.location.pathname,
    referrer: document.referrer || null,
    screenResolution: `${screen.width}x${screen.height}`,
    browserLanguage: navigator.language,
    timestamp: new Date().toISOString(),
  };
};

// Client-side compatible logger configuration
const logger = pino({
  level: getLogLevel(), // Use environment variable or fallback to default behavior
  // Remove pino-pretty transport as it's not suitable for client-side apps
  browser: {
    asObject: true, // Return logs as objects for easier handling
    write: {
      // Custom write function for browser environment
      debug: (o) => console.debug('[CLIENT-DEBUG]', o),
      info: (o) => console.info('[CLIENT-INFO]', o),
      warn: (o) => console.warn('[CLIENT-WARN]', o),
      error: (o) => console.error('[CLIENT-ERROR]', o),
    }
  },
  base: {
    env: process.env.NEXT_PUBLIC_DEV_MODE,
    environment: 'client',
  },
});

export interface LoggerInstance {
  debug: (msg: string, obj?: Record<string, unknown>) => void;
  info: (msg: string, obj?: Record<string, unknown>) => void;
  warn: (msg: string, obj?: Record<string, unknown>) => void;
  error: (msg: string, obj?: Record<string, unknown>) => void;
}

// Report to Sentry (replaces the old MongoDB error_logs sink).
// - error becomes a Sentry Issue (captureException when an Error is present,
//   otherwise captureMessage).
// - all levels are forwarded to the Sentry Logs product via Sentry.logger.*.
const reportToSentry = (
  level: 'debug' | 'info' | 'warn' | 'error',
  message: string,
  context: string,
  extra: Record<string, unknown> = {}
): void => {
  const attributes = { context, ...getClientMetadata(), ...sanitizeAttributes(extra) };

  // Structured log for the Logs product (best-effort; never throw from logging).
  try {
    Sentry.logger[level]?.(message, attributes);
  } catch { /* ignore */ }

  // Only error creates an Issue. warn/info stay in the Logs product: they are
  // expected conditions (no credits, declined payment, password-protected PDF)
  // and were drowning real errors in the Sentry issue stream.
  if (level !== 'error') return;

  try {
    const err = extra.error;
    if (err instanceof Error) {
      Sentry.captureException(err, {
        level: 'error',
        tags: { context },
        extra: { message, ...attributes },
      });
    } else {
      Sentry.captureMessage(message, {
        level: 'error',
        tags: { context },
        extra: attributes,
      });
    }
  } catch { /* ignore — logging must never break the app */ }
};

// Sentry log attributes must be primitives; coerce anything else to a string.
const sanitizeAttributes = (extra: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(extra)) {
    // Only an Error instance is reported separately (as the exception). Any
    // other value (e.g. a caller passing `error: err.message`) must survive —
    // dropping it silently strips the cause from every captureMessage Issue.
    if (k === 'error' && v instanceof Error) continue;
    out[k] = ['string', 'number', 'boolean'].includes(typeof v) ? v : String(v);
  }
  return out;
};

// Create context-specific loggers for CLIENT-SIDE
export const createLogger = (context: string): LoggerInstance => {
  return {
    debug: (msg: string, obj: Record<string, unknown> = {}) => {
      logger.debug({ context, ...obj }, msg);
      // Console only; not forwarded to Sentry to avoid noise.
    },
    info: (msg: string, obj: Record<string, unknown> = {}) => {
      logger.info({ context, ...obj }, msg);
      reportToSentry('info', msg, context, obj);
    },
    warn: (msg: string, obj: Record<string, unknown> = {}) => {
      logger.warn({ context, ...obj }, msg);
      reportToSentry('warn', msg, context, obj);
    },
    error: (msg: string, obj: Record<string, unknown> = {}) => {
      logger.error({ context, ...obj }, msg);
      reportToSentry('error', msg, context, obj);
    },
  };
};

// Default CLIENT-SIDE logger
export default logger;
